import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import TopNav from "../components/TopNav";
import { FaUser, FaPlane, FaClock, FaEnvelope, FaCalendarAlt, FaInfoCircle, FaLock, FaIdCard, FaHome, FaGlobe } from "react-icons/fa";
import "./UserProfilePage.css";

/**
 * UserProfilePage Component
 * 
 * A modern, aviation-themed user profile management interface.
 * Allows users to edit their profile descriptions and settings including:
 * - Hub selection (ICAO codes)
 * - Public bio
 * - VATSIM ID
 * - Home location
 * - Timezone preferences
 * - Email preferences
 * - Leave of absence status
 * 
 * Features:
 * - Dark mode support via props.darkmode
 * - Responsive card-based layout
 * - Progress modal for save operations
 * - Input validation
 * - Accessible with sequential tab indexing
 * - Timezone auto-detection
 * 
 * CSS classes are prefixed with 'upp-' (User Profile Page) to avoid conflicts
 */
const UserProfilePage = (props) => {

    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [progress, setProgress] = useState(0);
    const [modalMessage, setModalMessage] = useState('');
    const bioRef = useRef(null);
    const hubRef = useRef(null);
    const vatsimRef = useRef(null);
    const homeLocRef = useRef(null);
    const timeOffsetRef = useRef(null);
    const emailPrefRef = useRef(null);
    const loaRef = useRef(null);

    // Hub options with ICAO codes and corresponding cities
    const hubOptions = [
        { code: 'PANC', city: 'Anchorage' },
        { code: 'KOAK', city: 'Oakland' },
        { code: 'LFPG', city: 'Paris' },
        { code: 'KEWR', city: 'Newark' },
        { code: 'KMEM', city: 'Memphis' },
        { code: 'ZGGG', city: 'Guangzhou' },
        { code: 'EBLG', city: 'Liège' },
        { code: 'OMDB', city: 'Dubai' },
        { code: 'RJBB', city: 'Osaka' },
        { code: 'KIND', city: 'Indianapolis' }
    ];

    // Timezone options for selection
    const timezoneOptions = [
        { value: '-12.0', label: '(GMT -12:00) Baker Island' },
        { value: '-11.0', label: '(GMT -11:00) American Samoa' },
        { value: '-10.0', label: '(GMT -10:00) Hawaii' },
        { value: '-9.0', label: '(GMT -9:00) Alaska' },
        { value: '-8.0', label: '(GMT -8:00) Pacific' },
        { value: '-7.0', label: '(GMT -7:00) Mountain' },
        { value: '-6.0', label: '(GMT -6:00) Central' },
        { value: '-5.0', label: '(GMT -5:00) Eastern' },
        { value: '-4.0', label: '(GMT -4:00) Atlantic' },
        { value: '-3.0', label: '(GMT -3:00) Argentina' },
        { value: '-2.0', label: '(GMT -2:00) South Georgia' },
        { value: '-1.0', label: '(GMT -1:00) Azores' },
        { value: '0.0', label: '(GMT +0:00) London' },
        { value: '1.0', label: '(GMT +1:00) Berlin' },
        { value: '2.0', label: '(GMT +2:00) Cairo' },
        { value: '3.0', label: '(GMT +3:00) Moscow' },
        { value: '4.0', label: '(GMT +4:00) Dubai' },
        { value: '5.0', label: '(GMT +5:00) Karachi' },
        { value: '6.0', label: '(GMT +6:00) Dhaka' },
        { value: '7.0', label: '(GMT +7:00) Bangkok' },
        { value: '8.0', label: '(GMT +8:00) Beijing' },
        { value: '9.0', label: '(GMT +9:00) Tokyo' },
        { value: '10.0', label: '(GMT +10:00) Sydney' },
        { value: '11.0', label: '(GMT +11:00) Solomon Islands' },
        { value: '12.0', label: '(GMT +12:00) Auckland' }
    ];

    // Detect user's timezone
    const userOffset = (new Date().getTimezoneOffset() / 60) * -1;
    const detectedTimezoneOption = timezoneOptions.find(tz => parseFloat(tz.value) === userOffset);
    const detectedTimezone = detectedTimezoneOption?.label || 'Unknown';

    useEffect(() => {
        getUserProfile();
    }, [])

    const getUserProfile = async () => {
        console.log("entry");
        try {
            setLoading(true);
            const result = await axios.get("/api/get_user_profile", {
                params: { _t: Date.now() }
            });
            console.log(result.data)
            setData(result.data)
        }
        catch (e) {
            console.log(e)
            setError(e);
        }
        finally{
            setLoading(false);
        }
    }

    function handleChangePassword(){

        //window.location.href = 'https://fdxva.org/public/forms/reset_password.php';
        navigate('/reset-password');
        
    }

    function handleChangeEmail(){
        navigate('/reset-email');
        //console.log("handleChangeEmail")
        
        // navigate('/operations', { state: { refresh: true } });
    }    

    async function handleChange(item, field, ref){
        const value = ref.current.value;
        console.log("handleChange called:", { item, field, value });
        setShowModal(true);
        setProgress(0);
        setModalMessage('Updating...');
        const interval = setInterval(() => {
            setProgress(prev => Math.min(prev + 10, 90));
        }, 100);
        try {
            setLoading(true);
            const updateRequest = "/api/user/update_items";
            console.log("Sending to server:", { item, value });
            const response = await axios.post(updateRequest, { item, value });
            console.log("Server response:", response.data);
            clearInterval(interval);
            setProgress(100);
            setModalMessage('Change saved successfully!');
            setTimeout(() => setShowModal(false), 2000);
            //If successful, then success is an epoch value, otherwise it's 0
            if(response){
                console.log("Response received:", response.data);
            }
            if(response.data?.success > 0){
                // Update local data state
                console.log("Success! Updating local state:", { item, field, value });
                if(item === "emailPref"){
                    // Server logic: if value==0, set to 1; else set to 0
                    // So sending '0' means user wants Yes (emailPireps=1)
                    // Sending '1' means user wants No (emailPireps=0)
                    const newEmailPref = value === '0' ? 1 : 0;
                    setData(prevData => ({ ...prevData, [field]: newEmailPref }));
                } else if(item === "date_loa"){
                    // Server logic: if value==0, set to time(); else set to 0
                    // So sending '0' means user wants LOA (date_loa=current time)
                    // Sending '1' means user wants Active (date_loa=0)
                    if(value === '0'){
                        setData(prevData => ({ ...prevData, [field]: Math.floor(Date.now() / 1000) }));
                    } else {
                        setData(prevData => ({ ...prevData, [field]: 0 }));
                    }
                } else {
                    setData(prevData => ({ ...prevData, [field]: value }));
                }
            } else {
                console.log("Server returned success <= 0:", response.data);
            }
        } catch (e) {
            clearInterval(interval);
            setProgress(100);
            setModalMessage('Error: ' + (e.message || 'An error occurred'));
            setTimeout(() => setShowModal(false), 2000);
            setError(e.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }

    const setDetectedTimezone = () => {
        if (timeOffsetRef.current && detectedTimezoneOption) {
            timeOffsetRef.current.value = detectedTimezoneOption.value;
        }
    };

    return(
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <main className={`upp-container ${props.darkmode ? 'upp-dark' : ''}`} role="main" aria-label="User Profile Page">
                {error && <div className="upp-error-message" role="alert">Error: {error.message}</div>}
                
                {loading ? (
                    <div className="upp-loading-container">
                        <div className="upp-loading-spinner" aria-label="Loading"></div>
                        <p>Loading profile...</p>
                    </div>
                ) : data && (
                    <>
                        {/* Header Section */}
                        <header className="upp-header">
                            <div className="upp-header-content">
                                <FaUser className="upp-header-icon" aria-hidden="true" />
                                <h1>User Profile</h1>
                            </div>
                            <p className="upp-header-subtitle">Manage your FDX Virtual Airlines account settings</p>
                        </header>

                        {/* User Info Card */}
                        <section className="upp-info-section" aria-label="User Information">
                            <div className="upp-info-card">
                                <div className="upp-info-grid">
                                    <div className="upp-info-item">
                                        <span className="upp-info-label">Name</span>
                                        <span className="upp-info-value">{data.fullname}</span>
                                    </div>
                                    <div className="upp-info-item">
                                        <span className="upp-info-label">Callsign</span>
                                        <span className="upp-info-value">{data.callsign}</span>
                                    </div>
                                    <div className="upp-info-item">
                                        <span className="upp-info-label">Join Date</span>
                                        <span className="upp-info-value">{data.date_join ? new Date(data.date_join * 1000).toLocaleDateString() : 'N/A'}</span>
                                    </div>
                                    <div className="upp-info-item">
                                        <span className="upp-info-label">{data.myhub} Hub Manager</span>
                                        <span className="upp-info-value">{data.hub_manager}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Profile Cards Grid */}
                        <section className="upp-cards-grid" aria-label="Profile Settings">
                            {/* Public Bio Card */}
                            <article className="upp-card upp-card-wide">
                                <div className="upp-card-header">
                                    <FaIdCard className="upp-card-icon" aria-hidden="true" />
                                    <h3>Public Bio</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="bio">Bio (max 1024 characters)</label>
                                        <textarea
                                            id="bio"
                                            ref={bioRef}
                                            defaultValue={data.mybio}
                                            maxLength={1024}
                                            tabIndex={1}
                                            rows={4}
                                            placeholder="Tell us about yourself..."
                                            aria-describedby="bio-help"
                                        />
                                        <small id="bio-help" className="upp-form-help">Share your aviation experience and interests</small>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('bio', 'mybio', bioRef)} 
                                            tabIndex={2}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Update Bio
                                        </button>
                                    </div>
                                </div>
                            </article>

                            {/* Hub Selection Card */}
                            <article className="upp-card">
                                <div className="upp-card-header">
                                    <FaPlane className="upp-card-icon" aria-hidden="true" />
                                    <h3>Hub</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="hub">Select Your Hub</label>
                                        <select 
                                            id="hub" 
                                            ref={hubRef} 
                                            defaultValue={data.myhub} 
                                            tabIndex={3}
                                            aria-label="Select hub airport"
                                            className="upp-select-large"
                                        >
                                            {hubOptions.map(option => (
                                                <option key={option.code} value={option.code}>
                                                    {option.code} - {option.city}
                                                </option>
                                            ))}
                                        </select>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('hub', 'myhub', hubRef)} 
                                            tabIndex={4}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Change Hub
                                        </button>
                                    </div>
                                </div>
                            </article>

                            {/* VATSIM ID Card */}
                            <article className="upp-card">
                                <div className="upp-card-header">
                                    <FaGlobe className="upp-card-icon" aria-hidden="true" />
                                    <h3>VATSIM ID</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="vatsim">VATSIM ID (numeric)</label>
                                        <input
                                            id="vatsim"
                                            type="text"
                                            ref={vatsimRef}
                                            defaultValue={data.myvatsim}
                                            tabIndex={5}
                                            placeholder="Enter your VATSIM ID"
                                            aria-describedby="vatsim-help"
                                        />
                                        <small id="vatsim-help" className="upp-form-help">Your VATSIM CID number</small>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('vatsim', 'myvatsim', vatsimRef)} 
                                            tabIndex={6}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Update VATSIM ID
                                        </button>
                                    </div>
                                </div>
                            </article>

                            {/* Home Location Card */}
                            <article className="upp-card">
                                <div className="upp-card-header">
                                    <FaHome className="upp-card-icon" aria-hidden="true" />
                                    <h3>Home Location</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="homeloc">ICAO Code (4 characters max)</label>
                                        <input
                                            id="homeloc"
                                            type="text"
                                            ref={homeLocRef}
                                            defaultValue={data.homeloc}
                                            maxLength={4}
                                            tabIndex={7}
                                            placeholder="e.g., KJFK"
                                            pattern="[A-Za-z0-9]{1,4}"
                                            style={{ textTransform: 'uppercase' }}
                                            aria-describedby="homeloc-help"
                                        />
                                        <small id="homeloc-help" className="upp-form-help">Your home airport ICAO code</small>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('homeloc', 'homeloc', homeLocRef)} 
                                            tabIndex={8}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Update Location
                                        </button>
                                    </div>
                                </div>
                            </article>

                            {/* Timezone Card */}
                            <article className="upp-card">
                                <div className="upp-card-header">
                                    <FaClock className="upp-card-icon" aria-hidden="true" />
                                    <h3>Timezone</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="timezone">Timezone Offset</label>
                                        <select
                                            id="timezone"
                                            ref={timeOffsetRef}
                                            key={`timezone-${data.timeOffset}`}
                                            defaultValue={data.timeOffset !== undefined ? parseFloat(data.timeOffset).toFixed(1) : undefined}
                                            tabIndex={9}
                                            aria-label="Select timezone"
                                            className="upp-select-large"
                                        >
                                            {timezoneOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="upp-timezone-info">
                                            <FaInfoCircle aria-hidden="true" />
                                            <span>Detected: {detectedTimezone}</span>
                                            <button 
                                                type="button"
                                                onClick={setDetectedTimezone} 
                                                tabIndex={10}
                                                className="upp-btn upp-btn-link"
                                                aria-label="Set timezone to detected value"
                                            >
                                                Use Detected
                                            </button>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('timeOffset', 'timeOffset', timeOffsetRef)} 
                                            tabIndex={11}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Update Timezone
                                        </button>
                                    </div>
                                </div>
                            </article>

                            {/* Email Preference Card */}
                            <article className="upp-card">
                                <div className="upp-card-header">
                                    <FaEnvelope className="upp-card-icon" aria-hidden="true" />
                                    <h3>Email Preference</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="emailPref">Receive PIREP Emails</label>
                                        <select
                                            id="emailPref"
                                            ref={emailPrefRef}
                                            key={`emailPref-${data.emailPireps}`}
                                            defaultValue={data.emailPireps === 1 ? "0" : "1"}
                                            tabIndex={12}
                                            aria-describedby="email-pref-help"
                                            className="upp-select-large"
                                        >
                                            <option value="0">Yes</option>
                                            <option value="1">No</option>
                                        </select>
                                        <small id="email-pref-help" className="upp-form-help">
                                            Receive email notifications for your flight reports
                                        </small>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('emailPref', 'emailPireps', emailPrefRef)} 
                                            tabIndex={13}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Update Preference
                                        </button>
                                    </div>
                                </div>
                            </article>

                            {/* Leave of Absence Card */}
                            <article className="upp-card">
                                <div className="upp-card-header">
                                    <FaCalendarAlt className="upp-card-icon" aria-hidden="true" />
                                    <h3>Leave of Absence</h3>
                                </div>
                                <div className="upp-card-body">
                                    <div className="upp-form-group">
                                        <label htmlFor="loa">Leave of Absence Status</label>
                                        <select
                                            id="loa"
                                            ref={loaRef}
                                            key={`loa-${data.date_loa}`}
                                            defaultValue={data.date_loa >= 1 ? "0" : "1"}
                                            tabIndex={14}
                                            aria-describedby="loa-help"
                                            className="upp-select-large"
                                        >
                                            <option value="0">Yes - On Leave</option>
                                            <option value="1">No - Active</option>
                                        </select>
                                        <small id="loa-help" className="upp-form-help">
                                            Enable if you need to take a break from flying
                                        </small>
                                        <button 
                                            type="button"
                                            onClick={() => handleChange('date_loa', 'date_loa', loaRef)} 
                                            tabIndex={15}
                                            className="upp-btn upp-btn-primary"
                                        >
                                            Update Status
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </section>

                        {/* Promotions Info Section */}
                        <section className="upp-promotions-section" aria-label="Promotions Information">
                            <div className="upp-promotions-card">
                                <FaInfoCircle className="upp-info-icon" aria-hidden="true" />
                                <p>
                                    Promotions up to Captain are automatic. The system updates each day around 1730Z. 
                                    Promotions to Captain and above are handled by your hub manager, {data.hub_manager}.
                                </p>
                            </div>
                        </section>

                        {/* Account Actions Section - Change Password, Change Email, and Logout */}
                        <section className="upp-account-actions-section" aria-label="Account Actions">
                            <button 
                                type="button"
                                onClick={handleChangePassword} 
                                tabIndex={16}
                                className="upp-btn upp-btn-secondary"
                                aria-label="Change password"
                            >
                                <FaLock aria-hidden="true" />
                                <span>Change Password</span>
                            </button>
                            <button 
                                type="button"
                                onClick={handleChangeEmail} 
                                tabIndex={17}
                                className="upp-btn upp-btn-secondary"
                                aria-label="Change email"
                            >
                                <FaEnvelope aria-hidden="true" />
                                <span>Change Email</span>
                            </button>
                            {/* <button 
                                type="button"
                                onClick={handleLogout} 
                                className="upp-btn-logout" 
                                tabIndex={18}
                                aria-label="Logout from your account"
                            >
                                <FaSignOutAlt aria-hidden="true" />
                                <span>Logout</span>
                            </button> */}
                        </section>
                    </>
                )}

                {/* Progress Modal */}
                {showModal && (
                    <div className="upp-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                        <div className="upp-modal">
                            <div className="upp-modal-content">
                                <h4 id="modal-title">{progress < 100 ? 'Saving Changes' : 'Complete'}</h4>
                                <p className="upp-modal-message">{modalMessage}</p>
                                <div className="upp-progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                                    <div 
                                        className="upp-progress-fill" 
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                                <span className="upp-progress-text">{progress}%</span>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </React.Fragment>
    );
}

export default UserProfilePage;
